import { useSearchParams } from "@solidjs/router";
import {
  createMemo,
  createResource,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";

import { fetchFirms, fetchSectors, type FirmSummary } from "../api";
import FirmCard from "../components/FirmCard";
import Filters from "../components/Filters";

export default function Firms() {
  const [firmsRes] = createResource(fetchFirms);
  const [sectorsRes] = createResource(fetchSectors);

  const [searchParams, setSearchParams] = useSearchParams<{
    q?: string;
    sector?: string;
  }>();

  const search = () => searchParams.q ?? "";
  const sector = () => searchParams.sector ?? "";

  const setSearch = (v: string) =>
    setSearchParams({ q: v || undefined }, { replace: true });
  const setSector = (v: string) =>
    setSearchParams({ sector: v || undefined }, { replace: true });

  const filtered = createMemo<FirmSummary[]>(() => {
    const items = firmsRes()?.items ?? [];
    const q = search().trim().toLowerCase();
    const sec = sector();
    const matched = items.filter((f) => {
      if (sec && f.sector !== sec) return false;
      if (q) {
        const hay = `${f.name} ${f.sector ?? ""} ${f.hq ?? ""} ${f.ticker ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return matched.slice().sort((a, b) => {
      const ra = a.f500_rank ?? Number.POSITIVE_INFINITY;
      const rb = b.f500_rank ?? Number.POSITIVE_INFINITY;
      if (ra !== rb) return ra - rb;
      return a.name.localeCompare(b.name);
    });
  });

  onMount(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const t = e.target;
      const typing =
        t instanceof HTMLInputElement ||
        t instanceof HTMLTextAreaElement ||
        (t instanceof HTMLElement && t.isContentEditable);
      if (typing) return;

      if (e.key === "/") {
        e.preventDefault();
        const input = document.getElementById("search-input") as HTMLInputElement | null;
        if (input) {
          input.focus();
          input.select();
        }
      }
    };
    document.addEventListener("keydown", handler);
    onCleanup(() => document.removeEventListener("keydown", handler));
  });

  return (
    <>
      <Filters
        sectors={sectorsRes() ?? []}
        search={search()}
        sector={sector()}
        onSearch={setSearch}
        onSector={setSector}
      />

      <section class="grid">
        <Show
          when={!firmsRes.loading}
          fallback={<div class="loading">loading firms...</div>}
        >
          <For each={filtered()} fallback={<div class="empty">no firms match</div>}>
            {(f) => <FirmCard firm={f} />}
          </For>
        </Show>
      </section>
    </>
  );
}
